import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

const PACIFIC_MORNING_HOUR = 8
const PACIFIC_EVENING_HOUR = 20

export function getPacificUtcOffset() {
  const now = new Date()
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    timeZoneName: "short"
  })
  const parts = fmt.formatToParts(now)
  const tz = parts.find(p => p.type === "timeZoneName").value
  return tz === "PDT" ? 7 : 8
}

function pacificHourToLocalHour(pacificHour) {
  const offset = getPacificUtcOffset()
  const utcHour = pacificHour + offset
  const now = new Date()
  const asUtc = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour, 0))
  return asUtc.getHours()
}

export function pacificHourToLocalString(hour) {
  const h = pacificHourToLocalHour(hour)
  const period = h >= 12 ? "PM" : "AM"
  const display = h % 12 === 0 ? 12 : h % 12
  return `${display}:00 ${period}`
}

export function localToUtcString(localDate) {
  if (!localDate) return ""
  const y = localDate.getUTCFullYear()
  const m = String(localDate.getUTCMonth() + 1).padStart(2, "0")
  const d = String(localDate.getUTCDate()).padStart(2, "0")
  const h = String(localDate.getUTCHours()).padStart(2, "0")
  const min = String(localDate.getUTCMinutes()).padStart(2, "0")
  return `${y}-${m}-${d} ${h}:${min}`
}

export function isInPast(localDate) {
  return localDate.getTime() < Date.now()
}

export default class extends Controller {
  static targets = ["picker", "hidden", "tzLabel", "error", "submit", "morningBtn", "eveningBtn"]

  connect() {
    this.fp = flatpickr(this.pickerTarget, {
      enableTime: true,
      time_24hr: true,
      dateFormat: "Y-m-d H:i",
      allowInput: true,
      disableMobile: true,
      onChange: (_selectedDates, _dateStr, instance) => {
        this.syncFromPicker(instance)
      },
      onClose: (_selectedDates, _dateStr, instance) => {
        this.syncFromPicker(instance)
      }
    })

    // Button labels are set in the HAML template with FA icons; no override needed

    const fmt = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
    const tzPart = fmt.formatToParts(new Date()).find(p => p.type === "timeZoneName")
    this.tzLabelTarget.textContent = tzPart.value

    this.element.addEventListener("submit", (e) => this.handleSubmit(e))
  }

  disconnect() {
    if (this.fp) this.fp.destroy()
  }

  syncFromPicker(instance) {
    const selected = instance.selectedDates[0]
    if (!selected) {
      this.hiddenTarget.value = ""
      this.clearError()
      return
    }

    const utcStr = localToUtcString(selected)
    this.hiddenTarget.value = utcStr
    this.validate(selected)
  }

  setShortcutTime(pacificHour) {
    const current = this.fp.selectedDates[0]
    if (!current) {
      this.showError("Select a date first")
      return
    }

    const localHour = pacificHourToLocalHour(pacificHour)
    const newDate = new Date(current.getFullYear(), current.getMonth(), current.getDate(), localHour, 0, 0)
    this.fp.setDate(newDate, true)
    this.syncFromPicker(this.fp)
  }

  morning() {
    this.setShortcutTime(PACIFIC_MORNING_HOUR)
  }

  evening() {
    this.setShortcutTime(PACIFIC_EVENING_HOUR)
  }

  validate(localDate) {
    if (isInPast(localDate)) {
      this.showError("Cannot schedule in the past")
      return false
    }
    this.clearError()
    return true
  }

  showError(msg) {
    this.errorTarget.textContent = msg
    this.errorTarget.style.display = "block"
    this.submitTarget.disabled = true
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.style.display = "none"
    this.submitTarget.disabled = false
  }

  handleSubmit(e) {
    const selected = this.fp.selectedDates[0]
    if (selected && !this.validate(selected)) {
      e.preventDefault()
    }

    if (this.pickerTarget.value && !selected) {
      const parsed = this.fp.parseDate(this.pickerTarget.value, "Y-m-d H:i")
      if (parsed) {
        this.hiddenTarget.value = localToUtcString(parsed)
        if (!this.validate(parsed)) {
          e.preventDefault()
        }
      }
    }
  }
}
